import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { NOTARY_PRODUCTS, NOTARY_PRODUCT_CATEGORIES, NOTARY_CATALOG_PRICE_MIN, NOTARY_CATALOG_PRICE_MAX } from "@/data/notary-products";
import { BRAND_URL } from "@/lib/brand";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const TITLE = "บริการ Notary Public ทุกประเภท | รับรองลายมือชื่อ · Affidavit · POA | NYC";
const DESC = `แค็ตตาล็อกบริการรับรองเอกสารโดยทนาย Notarial Services ครบ ${NOTARY_PRODUCTS.length} รายการ — รับรองลายมือชื่อ, สำเนา, Affidavit, POA, Corporate Resolution, งานแปล · 1 วันทำการ · จดทะเบียนสภาทนายความไทย — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;

export const Route = createFileRoute("/notary/products/")({
  head: () => {
    const url = `${BRAND_URL}/notary/products`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/notary/products/index", "th")}, { name: "description", content: ctaDesc(DESC, "/notary/products/index", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/notary/products/index", "th")}, { property: "og:description", content: ctaDesc(DESC, "/notary/products/index", "th")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "en", href: `${BRAND_URL}/en/notary/products` },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "CollectionPage", "@id": `${url}#collection`,
            url, name: TITLE, inLanguage: "th-TH",
            numberOfItems: NOTARY_PRODUCTS.length,
          }, {
            "@type": "ItemList", "@id": `${url}#itemlist`,
            itemListElement: NOTARY_PRODUCTS.map((p, i) => ({
              "@type": "ListItem", position: i + 1,
              url: `${BRAND_URL}/notary/products/${p.slug}`, name: p.nameTh,
            })),
          }, {
            "@type": "OfferCatalog", "@id": `${url}#catalog`, name: TITLE,
            itemListElement: NOTARY_PRODUCTS.map((p) => ({
              "@type": "Offer", name: p.nameTh, sku: p.sku,
              priceCurrency: "THB", price: p.priceMid,
              url: `${BRAND_URL}/notary/products/${p.slug}`,
            })),
          }],
        }),
      }],
    };
  },
  component: CatalogTH,
});

function CatalogTH() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-indigo-100/60 to-background px-4 pt-16 pb-10">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-bold text-indigo-700">Notary Product Catalog</span>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl">บริการรับรองเอกสารโดยทนาย Notary — {NOTARY_PRODUCTS.length} รายการ</h1>
          <p data-speakable className="direct-answer mt-4 max-w-3xl rounded-xl border-l-4 border-indigo-400 bg-indigo-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
            ราคาเริ่ม ฿{NOTARY_CATALOG_PRICE_MIN.toLocaleString()}–฿{NOTARY_CATALOG_PRICE_MAX.toLocaleString()} · เสร็จภายใน 1 วันทำการ · จดทะเบียนสภาทนายความไทย ใช้ยื่นต่อ MFA / Apostille / สถานทูตได้ทันที
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-10">
        {NOTARY_PRODUCT_CATEGORIES.map((c) => (
          <div key={c.id} className="mb-10">
            <h2 className="mb-4 font-display text-xl font-extrabold text-navy-deep">
              {c.labelTh} <span className="text-sm font-normal text-slate-500">({c.count} รายการ)</span>
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {NOTARY_PRODUCTS.filter((p) => p.category === c.id).map((p) => (
                <Link key={p.sku} to="/notary/products/$slug" params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-indigo-400 hover:shadow-md">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">{p.sku}</div>
                  <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-indigo-700">{p.nameTh}</div>
                  <div className="mt-2 text-xs text-slate-600 line-clamp-2">{p.shortDescTh}</div>
                  <div className="mt-3 text-xs font-semibold text-emerald-700">฿{p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()} · {p.act.turnaround}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
      <ProductCatalogHubNav locale="th" current="notary" />
    <HubLeadCTA locale="th" service="Notary Public" />
      </main>
  );
}
